import { radius as radiusLookup } from "@bluehive/zipcodes";
import { Lens } from "@hookform/lenses";
import { ExpandMore } from "@mui/icons-material";
import {
  InputAdornment,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import { uniq } from "lodash";
import { ReactElement, useEffect } from "react";
import { useWatch, useController, useFormContext } from "react-hook-form";

import { AudienceFilterConfig } from "@stw/yup-validators";

import { FlexBox } from "~frontend/components/FlexBox";
import theme, { palette } from "~frontend/theme";
import { plural } from "~frontend/util/plural";

import { RhfCommaSeparatedTextField } from "./RhfCommaSeparatedTextField";
import { RhfTextField } from "./RhfTextField";

export function RhfZipCodeRadius({
  filterConfigLens,
  lookupResultsLens,
  fullWidth = true,
  bottomLabel,
  isSlim = false,
}: {
  filterConfigLens: Lens<AudienceFilterConfig>;
  lookupResultsLens: Lens<string[]>;
  fullWidth?: boolean;
  bottomLabel?: string;
  isSlim?: boolean;
}): ReactElement {
  const { setValue } = useFormContext();

  const centerZipsController = filterConfigLens.focus("centerZips").interop();
  const centerZips = useWatch(centerZipsController);

  const radiusController = filterConfigLens.focus("radius").interop();
  const radius = useWatch(filterConfigLens.focus("radius").interop());

  const { field: lookupResultsField } = useController(
    lookupResultsLens.interop()
  );
  const lookupResults = useWatch(lookupResultsLens.interop());

  useEffect(() => {
    if (Array.isArray(centerZips)) {
      const results = centerZips.reduce((acc, zip) => {
        const zips = radiusLookup(zip, Number(radius));
        return [...acc, ...zips];
      }, [] as string[]);

      setValue(lookupResultsField.name, uniq(results).sort());
    }
  }, [centerZips, radius, setValue, lookupResultsField.name]);

  return (
    <FlexBox direction="column">
      <FlexBox gap={2}>
        <RhfCommaSeparatedTextField
          {...centerZipsController}
          label="Zip Code"
          fullWidth={fullWidth}
          variant={isSlim ? "standard" : "outlined"}
          slotProps={
            isSlim ? { input: { sx: { fontSize: "12px" } } } : undefined
          }
        />
        <RhfTextField
          {...radiusController}
          label="Radius"
          fullWidth={fullWidth}
          variant={isSlim ? "standard" : "outlined"}
          slotProps={{
            htmlInput: {
              step: 5,
              min: 0,
            },
            input: {
              endAdornment: (
                <InputAdornment position="end" sx={{ paddingRight: "5px" }}>
                  <Typography component={"span"}>miles</Typography>
                </InputAdornment>
              ),
              sx: isSlim ? { fontSize: "12px" } : undefined,
            },
          }}
        />
      </FlexBox>
      {bottomLabel && (
        <Typography variant="caption" color="textSecondary">
          {bottomLabel}
        </Typography>
      )}
      {radius &&
        Array.isArray(centerZips) &&
        centerZips.length > 0 &&
        Array.isArray(lookupResults) && (
          <Accordion
            square={true}
            sx={{
              marginTop: theme.spacing(1),
              padding: 0,
              color: "darkGray",
              "& .MuiAccordionSummary-root": { minHeight: "auto" },
              "&.Mui-expanded": { margin: theme.spacing(1, 0, 0, 0) },
              "& .MuiAccordionSummary-content": { margin: 0 },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMore sx={{ fontSize: "15px" }} />}
              sx={{
                padding: theme.spacing(0, 0, 1, 0),
                borderBottom: "1px solid",
                borderBottomColor: `${palette.darkGray}88`,
              }}
            >
              <Typography component={"span"} variant="body3Bold">
                {plural(lookupResults.length, "zip code", "zip codes")} within{" "}
                {plural(Number(radius), "mile", "miles")} of{" "}
                {centerZips.join(", ")}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <RhfTextField
                {...lookupResultsLens.interop()}
                fullWidth={true}
                multiline={true}
                minRows={2}
                sx={{
                  paddingTop: 0,
                  "& .MuiInputBase-root": { border: "none" },
                }}
                slotProps={{
                  input: {
                    readOnly: true,
                    sx: {
                      "& .MuiInputBase-readOnly": {
                        background: palette.lightGray,
                        padding: 0,
                      },
                    },
                  },
                }}
              />
            </AccordionDetails>
          </Accordion>
        )}
    </FlexBox>
  );
}
